import { useState, type FormEvent, type KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";

import { Chevron } from "@/components/ui/Chevron";
import { ApiError } from "@/lib/api/client";
import { extractScenario, startSession } from "@/lib/api/sessions";
import { useSessionStore } from "@/lib/state/session";

/**
 * Chat-driven scenario capture (Step 5).
 *
 * Asks three short questions in sequence. After the third answer, posts
 * the Q/A pairs to /scenario/extract, which runs an LLM call to produce
 * a structured ScenarioBrief. We then call /sessions with that brief and
 * navigate to the session view, as the legacy ScenarioForm did.
 *
 * v1: questions are hardcoded. Future work can either fetch them from
 * the backend or have the LLM generate follow-ups dynamically.
 */
const QUESTIONS = [
  "Tell me about your integration. What system is sending the data, and what kind of data is it?",
  "Where does it need to land? (e.g. another app, a data warehouse, an API)",
  "Any constraints I should know about — ordering, throughput, latency, compliance?",
];

interface Message {
  role: "assistant" | "user";
  text: string;
}

export function ChatThread() {
  const navigate = useNavigate();
  const reset = useSessionStore((s) => s.reset);

  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", text: QUESTIONS[0] },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const askedCount = messages.filter((m) => m.role === "assistant").length;
  const answeredCount = messages.filter((m) => m.role === "user").length;
  const allAnswered = answeredCount >= QUESTIONS.length;

  async function submit() {
    const text = input.trim();
    if (!text || busy) return;
    setError(null);
    setInput("");

    const nextMessages: Message[] = [
      ...messages,
      { role: "user", text },
    ];

    const nextAnswered = answeredCount + 1;
    if (nextAnswered < QUESTIONS.length) {
      nextMessages.push({
        role: "assistant",
        text: QUESTIONS[nextAnswered],
      });
      setMessages(nextMessages);
      return;
    }

    // Final answer — post all Q/A pairs and proceed.
    setMessages([
      ...nextMessages,
      { role: "assistant", text: "Thanks. Setting up the scan…" },
    ]);
    setBusy(true);
    try {
      const turns = QUESTIONS.map((q, i) => ({
        q,
        a:
          nextMessages
            .filter((m) => m.role === "user")
            [i]?.text ?? "",
      }));
      const extracted = await extractScenario({ turns });
      const startResp = await startSession({ scenario: extracted.scenario });
      reset(startResp.session_id);
      navigate(`/sessions/${startResp.session_id}`);
    } catch (err) {
      let msg = "Unable to start session.";
      if (err instanceof ApiError && err.isAuthError) {
        msg = "Sign-in expired. Refresh the page and try again.";
      } else if (err instanceof Error) {
        msg = err.message;
      }
      setError(msg);
      setBusy(false);
    }
  }

  function onFormSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    void submit();
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void submit();
    }
  }

  return (
    <section className="rounded-lg border border-border/60 bg-card p-6 space-y-4">
      <header>
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
          <Chevron className="mr-2" /> describe your integration
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Three quick questions — answer in your own words.
        </p>
      </header>

      <div className="space-y-3 max-h-[420px] overflow-y-auto">
        {messages.map((m, i) => (
          <Bubble key={i} role={m.role} text={m.text} />
        ))}
      </div>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      {!allAnswered ? (
        <form onSubmit={onFormSubmit} className="space-y-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            rows={3}
            placeholder={`Question ${answeredCount + 1} of ${QUESTIONS.length}…`}
            disabled={busy}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Enter to send · Shift+Enter for newline
            </p>
            <button
              type="submit"
              disabled={busy || !input.trim()}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              <Chevron className="mr-1.5 text-primary-foreground" />
              {answeredCount + 1 === QUESTIONS.length ? "Send" : "Next"}
            </button>
          </div>
        </form>
      ) : (
        <p className="text-sm text-muted-foreground italic">
          {busy ? "Working…" : null}
        </p>
      )}

      <p className="text-[10px] text-muted-foreground/70">
        Asked {askedCount}, answered {answeredCount}.
      </p>
    </section>
  );
}

function Bubble({ role, text }: { role: "assistant" | "user"; text: string }) {
  const isAssistant = role === "assistant";
  return (
    <div
      className={`rounded-md px-3 py-2 text-sm ${
        isAssistant
          ? "bg-primary/5 border border-primary/30"
          : "bg-secondary/40 border border-border/40 ml-8"
      }`}
    >
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
        {isAssistant ? "assistant" : "you"}
      </p>
      <p className="whitespace-pre-wrap leading-relaxed">{text}</p>
    </div>
  );
}
